import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../api/client';
import HeadlineBand from '../components/engine/HeadlineBand';

export default function Engine1_Formulation() {
  const pipeline = useQuery({ queryKey: ['formulation', 'pipeline'], queryFn: api.formulationPipeline });
  const [brief, setBrief] = useState('Zero-sugar functional convenience SKU under $0.55 cost-per-unit, on-brand floral, target Q2 launch.');
  const [costTo99c, setCostTo99c] = useState(true);
  const [claimsClean, setClaimsClean] = useState(true);
  const [packBigCan, setPackBigCan] = useState(true);

  const run = useMutation({
    mutationFn: () =>
      api.formulationRun(brief, {
        cost_to_99c: costTo99c,
        claims_clean: claimsClean,
        pack: packBigCan ? 'big_can_23oz' : 'multipack',
      }),
  });
  const headlineState = run.data ?? pipeline.data;

  return (
    <div>
      {headlineState && <HeadlineBand view="rapid_formulation" state={headlineState as unknown as Record<string, unknown>} />}

      {/* Pipeline KPIs */}
      <div className="grid2" style={{ marginBottom: 16 }}>
        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--amber)' }} />Pipeline state</div>
          {pipeline.data ? (
            <div className="row" style={{ gap: 24 }}>
              <div className="kpi">
                <div className="num">{pipeline.data.concepts_in_flight}</div>
                <div className="lab">concepts in flight</div>
              </div>
              <div className="kpi">
                <div className="num" style={{ color: pipeline.data.survival_rate >= 0.6 ? 'var(--olive)' : 'var(--gold)' }}>
                  {(pipeline.data.survival_rate * 100).toFixed(0)}%
                </div>
                <div className="lab">12-mo survival rate</div>
              </div>
            </div>
          ) : (
            <div className="small faint">Loading…</div>
          )}
        </div>

        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--gold)' }} />Rapid Formulation Studio</div>
          <p className="small" style={{ lineHeight: 1.55 }}>
            ML-powered flavor/recipe + pack-variant generation tuned to AriZona codes. Mirrors
            Coca-Cola Y3000 / Unilever AI reformulation. Cycle time: months → weeks.
          </p>
        </div>
      </div>

      {/* Brief intake */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="qhead"><span className="qdot" style={{ background: 'var(--blossom)' }} />Brief intake · run a concept slate</div>

        <div style={{ marginBottom: 12 }}>
          <div className="small faint" style={{ marginBottom: 4, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Brief
          </div>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            rows={3}
            className="c2-input"
            style={{ width: '100%', padding: '10px 13px', height: 'auto', resize: 'vertical' }}
          />
        </div>

        <div className="row" style={{ gap: 18, marginBottom: 12 }}>
          <label className="row" style={{ fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={costTo99c} onChange={(e) => setCostTo99c(e.target.checked)} />
            Cost-to-99¢ constraint
          </label>
          <label className="row" style={{ fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={claimsClean} onChange={(e) => setClaimsClean(e.target.checked)} />
            Label/claims clean
          </label>
          <label className="row" style={{ fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={packBigCan} onChange={(e) => setPackBigCan(e.target.checked)} />
            Big Can 23oz pack
          </label>
        </div>

        <button type="button" className="btn primary" onClick={() => run.mutate()} disabled={run.isPending}>
          {run.isPending ? 'Running…' : '⚡ Generate concept slate'}
        </button>
      </div>

      {/* Concept slate */}
      {run.data && (
        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--olive)' }} />Concept slate · {run.data.concepts.length} concepts · cycle {run.data.cycle_time}</div>
          <table>
            <thead>
              <tr>
                <th>Concept</th>
                <th style={{ width: 140, textAlign: 'right' }}>Cost / unit</th>
                <th style={{ width: 140, textAlign: 'right' }}>On-brand</th>
                <th style={{ width: 140 }}>Label clear</th>
              </tr>
            </thead>
            <tbody>
              {run.data.concepts.map((c, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600 }}>{c.name}</td>
                  <td className="mono" style={{ textAlign: 'right', color: c.cost_per_unit <= 0.55 ? 'var(--olive)' : 'var(--red)' }}>
                    ${c.cost_per_unit.toFixed(2)}
                  </td>
                  <td className="mono" style={{ textAlign: 'right', color: c.on_brand_score >= 0.8 ? 'var(--olive)' : 'var(--gold)' }}>
                    {(c.on_brand_score * 100).toFixed(0)}
                  </td>
                  <td>
                    {c.label_clear ? <span className="tag olive">✓ clear</span> : <span className="tag red">✗ review</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
